import React, { useMemo } from "react";
import { Dictionary } from "lodash";
import { CompetencyMapSection } from "@/shared/widgets/CompetencyMapSection";
import { DimensionWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { ScorecardRow } from "@/features/scorecards/graphql/generated";
import { ContentSection, FlowingContent } from "../../../generator";
import { DimensionContext } from "@/types/data";
import { useCompetencyInsights } from "@/shared/hooks/insights/useCompetencyInsights";
import { useCandidateCompetencyInsights } from "@/shared/hooks/insights/useCandidateCompetencyInsights";
import { Person } from "@/features/people/graphql/generated";
import { useProfileCompletion } from "@/features/profile/hooks/useProfileCompletion";
import { PdfInsightsSection } from "../../../components/PdfInsightGroupSection";
import { BaseInsight } from "@/shared/hooks/insights/Insight.types";

interface CompetenciesProps {
  dimensions: Dictionary<DimensionWithScores[]>;
  defaultPersonId?: number;
  person?: Person;
  scorecardRows?: ScorecardRow[];
  context: DimensionContext;
}

function Competencies(props: CompetenciesProps) {
  const { dimensions, context, defaultPersonId, person, scorecardRows } = props;

  const { competenciesComplete } = useProfileCompletion(dimensions);

  const candidateInsights = useCandidateCompetencyInsights({
    dimensions: dimensions.competencies,
    person,
    scorecardRows,
  });

  const regularInsights = useCompetencyInsights({
    dimensions: dimensions.competencies,
    person,
    scorecardRows,
    context,
  });

  const groupedInsights =
    context === "candidate" ? candidateInsights : regularInsights;

  const InsightsSections = useMemo(() => {
    return (
      <PdfInsightsSection
        groupedInsights={groupedInsights as Record<string, BaseInsight[]>}
        person={person}
      />
    );
  }, [groupedInsights, person]);

  if (!competenciesComplete) return null;

  return (
    <ContentSection>
      <CompetencyMapSection
        context={context}
        dimensions={dimensions.competencies}
        defaultPersonId={defaultPersonId}
      />
      <FlowingContent>{InsightsSections}</FlowingContent>
    </ContentSection>
  );
}

export { Competencies };
